import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Card, CardContent, Checkbox, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography, } from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrievePlayersByTeamId, useRetrieveTeam, useRetrieveUserTeamPermissions, useSetAssistantManagers, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { PlayerAvatar } from "../players/PlayerAvatar";
import { playerDisplayName } from "../players/PlayerDisplayNameLink";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export const ManageAssistantManagers = () => {
    const { id } = useNumericParams();
    const navigate = useNavigate();
    // null until the user changes something; then it holds their working selection.
    const [selected, setSelected] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const { data: permissions, isLoading: permsLoading, error: permsError, } = useRetrieveUserTeamPermissions({ teamId: id });
    const { canView = false, canModify = false } = permissions || {};
    const { data: team, isLoading: teamLoading, error: teamError, } = useRetrieveTeam({ id, enabled: canView });
    const { data: players, isLoading: playersLoading, error: playersError, } = useRetrievePlayersByTeamId({ teamId: id, enabled: canView });
    const setMutation = useSetAssistantManagers({
        teamId: id,
        onSuccess: () => navigate(`/teams/${id}/`),
        onError: () => setErrorMessage("Failed to update assistant managers."),
    });
    // Eligible = active registered members (have a user account) who aren't the
    // manager. Inactive/removed members can't be appointed.
    const eligible = useMemo(() => (players || [])
        .filter((p) => p.isActive && p.user?.pk != null && p.user.pk !== team?.manager)
        .map((p) => ({ player: p, userId: p.user.pk })), [players, team?.manager]);
    const current = useMemo(() => new Set(team?.assistantManagers ?? []), [team?.assistantManagers]);
    const working = selected ?? current;
    if (permsLoading || teamLoading || playersLoading) {
        return _jsx(Loading, {});
    }
    if (permsError || teamError || playersError) {
        return (_jsx(ErrorView, { error: permsError ||
                teamError ||
                playersError ||
                new Error("Failed to load.") }));
    }
    // Managing assistant managers is manager-only.
    if (!canView || !canModify) {
        return _jsx(Forbidden, {});
    }
    const toggle = (userId) => {
        const next = new Set(working);
        if (next.has(userId)) {
            next.delete(userId);
        }
        else {
            next.add(userId);
        }
        setSelected(next);
    };
    const dirty = selected != null &&
        (selected.size !== current.size ||
            [...selected].some((uid) => !current.has(uid)));
    return (_jsxs(React.Fragment, { children: [_jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "h4", sx: { mb: 1 }, children: "Assistant Managers" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", children: ["Select team members who can help enter", " ", _jsx(Link, { to: `/teams/${id}/`, style: { textDecoration: "none" }, children: team?.name }), "'s match stats. Assistant managers can enter stats only \u2014 they can't edit the team, roster, or transfers."] })] }), _jsx(Card, { children: _jsxs(CardContent, { children: [eligible.length === 0 ? (_jsx(Box, { sx: { textAlign: "center", py: 4 }, children: _jsx(Typography, { variant: "body1", color: "text.secondary", children: "No eligible members. Add registered players to the team first \u2014 only members with an account can be assistant managers." }) })) : (_jsx(List, { disablePadding: true, children: eligible.map(({ player, userId }) => (_jsx(ListItem, { disablePadding: true, secondaryAction: _jsx(Checkbox, { edge: "end", checked: working.has(userId), onChange: () => toggle(userId), inputProps: {
                                        "aria-label": `Assistant manager: ${playerDisplayName(player)}`,
                                    } }), children: _jsxs(ListItemButton, { onClick: () => toggle(userId), children: [_jsx(ListItemAvatar, { sx: { mr: 2 }, children: _jsx(PlayerAvatar, { player: player, size: 40 }) }), _jsx(ListItemText, { primary: playerDisplayName(player) })] }) }, player.id))) })), _jsxs(Box, { sx: {
                                mt: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                            }, children: [_jsx(Button, { component: Link, to: `/teams/${id}/`, variant: "outlined", children: "Cancel" }), _jsx(Button, { variant: "contained", disabled: !dirty || setMutation.isLoading, onClick: () => setMutation.mutate([...working]), children: "Save" })] })] }) }), _jsx(ErrorSnackbar, { open: !!errorMessage, message: errorMessage, onClose: () => setErrorMessage("") })] }));
};
